import type { LifestylePrize } from "@/app/redux/features/api/lifestyle.types";
import type { HeroPrize } from "../../hooks/useLifestyleCollections";
import { CartIcon, ChevronsRightIcon } from "../lifestyleIcons";

interface FeaturedSlideProps {
  prize: HeroPrize;
  /** Every slide is mounted, so only the one on screen at load fetches early. */
  eager?: boolean;
  onAddToCart: (prize: LifestylePrize) => void;
  /**
   * Logged-in mobile only: the how-it-works section under the hero is hidden,
   * so a compact link lives under the Enter now / basket row instead.
   */
  showHowItWorksLink?: boolean;
  onOpenHowItWorks?: () => void;
}

/** One hero slide: full-bleed banner image with overlay headline and CTAs. */
export default function FeaturedSlide({
  prize,
  eager,
  onAddToCart,
  showHowItWorksLink,
  onOpenHowItWorks,
}: FeaturedSlideProps) {
  return (
    <div className="relative w-full min-h-[360px] lg:min-h-[400px] aspect-square lg:aspect-[1920/400] overflow-hidden">
      <img
        src={prize.mobileImage}
        alt={prize.title}
        className="lg:hidden absolute inset-0 w-full h-full object-cover"
        loading={eager ? "eager" : "lazy"}
      />
      <img
        src={prize.desktopImage}
        alt=""
        aria-hidden="true"
        className="hidden lg:block absolute inset-0 w-full h-full object-cover"
        loading={eager ? "eager" : "lazy"}
      />

      {/*
       * Padding lives on the outer shell; the inner shell is a fixed max-width
       * track. Logged-in drops the vertical padding — the how-it-works link
       * already fills that space. Guests keep the original pt/pb.
       */}
      <div
        className={`absolute inset-0 z-[1] flex items-end lg:items-center w-full px-2.5 lg:px-16 ${
          showHowItWorksLink ? "" : "pt-10 pb-12 lg:py-20"
        }`}>
        <div className="flex flex-col items-center lg:items-start w-full max-w-[1320px] mx-auto gap-4 lg:gap-6">
          <div className="flex flex-col items-center lg:items-start w-full gap-1">
            <div className="flex flex-col items-center lg:items-start w-full gap-2 lg:gap-4">
              {prize.displayName && (
                <span className="px-[11px] py-0.5 lg:py-1 text-[8px] lg:text-xs font-medium uppercase tracking-[0] text-primary bg-black/60 border border-primary rounded">
                  {prize.displayName}
                </span>
              )}

              <h2 className="text-base lg:text-[28px] font-bold leading-[22px] lg:leading-8 uppercase text-white text-center lg:text-left !mb-0 break-words">
                {prize.title}
              </h2>
            </div>

            {prize.HasCashAlternative && !prize.HideCashAlternative && prize.CashAlternative && (
              <p className="w-full text-sm font-roboto font-normal leading-5 tracking-[0] text-gray-5 text-center lg:text-left">
                Or take <span className="font-semibold text-white">{prize.CashAlternative}</span> cash
              </p>
            )}
          </div>

          {/*
           * CTAs: Enter now + basket on one row. When logged in on mobile the
           * how-it-works card under the hero is gone, so its link wraps under
           * this row (Figma 15390:8697) — desktop stays section-only / hidden.
           */}
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 w-[222px] lg:w-auto">
            <a
              href={prize.Url}
              className="flex items-center justify-center h-10 lg:h-11 min-w-[170px] lg:min-w-0 px-3 gap-1.5 lg:gap-2 text-sm lg:text-base font-medium uppercase text-white border border-primary rounded hover:bg-primary/10 transition-colors">
              Enter now
              <ChevronsRightIcon className="w-4 h-4 lg:w-5 lg:h-5" />
            </a>

            <button
              type="button"
              onClick={() => onAddToCart(prize)}
              aria-label={`Add a ${prize.title} ticket to your basket`}
              className="flex items-center justify-center w-10 h-10 lg:w-11 lg:h-11 text-white bg-primary rounded hover:bg-primary/90 transition-colors">
              {/* Icon frame ~20×21; vector leaf 14.83×16.5 as in Figma */}
              <CartIcon className="w-[14.83px] h-[16.5px]" />
            </button>

            {showHowItWorksLink && onOpenHowItWorks && (
              <button
                type="button"
                onClick={onOpenHowItWorks}
                className="lg:hidden flex items-center justify-center w-40 h-9 px-2 text-sm font-medium leading-5 uppercase text-primary underline underline-offset-2 hover:text-primary/80 transition-colors">
                How it works
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
